import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Chip,
  Grid,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Send as SendIcon,
  VerifiedUser as ShieldIcon,
  Build as BuildIcon,
  AssignmentReturn as ReturnIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';

const ContractModal = ({ open, onClose, clientId, initialTab = 0, onOpenDeactivation }) => {
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  // Paginadores independientes por documento (0-indexed)
  const [contratoPageIndex, setContratoPageIndex] = useState(0);
  const [anexo1PageIndex, setAnexo1PageIndex] = useState(0);
  const [anexo2PageIndex, setAnexo2PageIndex] = useState(0);

  // Estados interactivos para Checkboxes de Anexo 1 (Instalación)
  const [anexo1Checkboxes, setAnexo1Checkboxes] = useState({
    cpeAntenna: true,
    wifiRouter: true,
    poeInjector: true,
    utpOutdoorCable: true,
    shieldedRj45: true,
    metallicMast: true,
    fixingKit: true,
    groundingSystem: true,
    weatherproofClamps: true
  });

  // Estados interactivos para Checkboxes de Anexo 2 (Devolución)
  const [anexo2Checkboxes, setAnexo2Checkboxes] = useState({
    cpeAntenna: true,
    wifiRouter: true,
    poeInjector: true,
    utpIndoorCable: true,
    metallicMast: true,
    conditionExcelente: false,
    conditionBueno: true,
    conditionDesgaste: false,
    conditionDamaged: false,
    conditionNotReturned: false
  });

  const printRef = useRef(null);

  useEffect(() => {
    if (open && clientId) {
      setActiveTab(initialTab);
      setContratoPageIndex(0);
      setAnexo1PageIndex(0);
      setAnexo2PageIndex(0);
      fetchContractData();
    }
  }, [open, clientId, initialTab]);

  const fetchContractData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/contracts/client/${clientId}/data`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        throw new Error('Error al cargar la información del contrato');
      }
      const data = await response.json();
      setContractData(data);
    } catch (err) {
      console.error('Error fetching contract data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAnexo1Check = (key) => {
    setAnexo1Checkboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleAnexo2Check = (key) => {
    setAnexo2Checkboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrintAll = () => {
    if (!contractData) return;
    const printContent = document.getElementById('contract-all-pages-printable');
    const docTitle = activeTab === 0 
      ? 'Contrato Completo (13 Páginas)' 
      : activeTab === 1 
      ? 'Anexo 1 - Ficha de Instalación (10 Páginas)' 
      : 'Anexo 2 - Acta de Devolución (6 Páginas)';

    const WinPrint = window.open('', '', 'width=980,height=980');
    WinPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${docTitle} ${contractData.contractNumber} - ${contractData.client.fullName}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 0; background-color: #ffffff; }
            .a4-page { width: 210mm; min-height: 295mm; padding: 16mm 16mm; margin: 0 auto 10mm auto; background: white; border: 1px solid #cbd5e1; position: relative; page-break-after: always; break-after: page; }
            .page-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #6366f1; padding-bottom: 8px; margin-bottom: 12px; }
            .page-footer { position: absolute; bottom: 10mm; left: 16mm; right: 16mm; border-top: 1px solid #cbd5e1; padding-top: 4px; display: flex; justify-content: space-between; font-size: 9px; color: #64748b; }
            h1, h2, h3, h4 { color: #0f172a; margin-top: 0; }
            .section-title { background-color: #f1f5f9; padding: 6px 10px; font-weight: 700; border-left: 4px solid #6366f1; margin: 14px 0 8px 0; font-size: 11px; color: #0f172a; text-transform: uppercase; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
            table, th, td { border: 1px solid #cbd5e1; }
            th, td { padding: 5px 7px; text-align: left; font-size: 10px; }
            th { background-color: #f8fafc; color: #334155; font-weight: 700; }
            .clause { margin-bottom: 8px; text-align: justify; font-size: 10.5px; line-height: 1.5; }
            .clause-title { font-weight: 700; color: #0f172a; }
            @page { size: A4 portrait; margin: 0; }
            @media print {
              body { background-color: #ffffff; padding: 0; }
              .a4-page { border: none; box-shadow: none; margin: 0; width: 100%; min-height: 100vh; padding: 12mm; page-break-after: always; break-after: page; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    WinPrint.document.close();
    WinPrint.focus();
    setTimeout(() => {
      WinPrint.print();
      WinPrint.close();
    }, 500);
  };

  const [emittingContract, setEmittingContract] = useState(false);

  const handleEmitAndSaveContract = async () => {
    if (!contractData || !clientId) return;
    setEmittingContract(true);
    try {
      const res = await fetch('/api/documents/emit-contract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clientId,
          documentData: contractData
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al emitir el contrato');
      alert('✅ ¡Contrato OSIPTEL emitido y guardado exitosamente en la pestaña Fichas del cliente!');
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setEmittingContract(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!contractData || !contractData.client.phone) {
      alert('El cliente no tiene un teléfono celular registrado.');
      return;
    }

    const cleanPhone = contractData.client.phone.replace(/\D/g, '');
    const message = 
      `Estimado(a) *${contractData.client.fullName}*, le enviamos la información de su contrato del Servicio de Internet YinoWISP: *${contractData.contractNumber}*.\n\n` +
      `📌 *Plan*: ${contractData.service.planName} (${contractData.service.downloadSpeed})\n` +
      `💰 *Renta Mensual*: S/ ${contractData.service.price}\n` +
      `📅 *Día Límite de Pago*: Día ${contractData.client.paymentDay} de cada mes\n` +
      `🌐 *Dirección IP*: ${contractData.client.ipAddress || 'Asignada en router'}\n\n` +
      `📱 *Puede consultar y descargar su contrato completo ingresando al Portal del Cliente*:\nhttps://cliente.yinowisp.com\n\n` +
      `¡Gracias por confiar en YinoWISP!`;

    try {
      setSendingWhatsapp(true);
      const res = await fetch('/api/whatsapp/send-custom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clientId: contractData.client.id,
          number: cleanPhone,
          message: message
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Error enviando mensaje');
      }

      alert('✅ ¡Contrato enviado exitosamente por WhatsApp desde el servidor VPS Baileys!');
    } catch (err) {
      console.warn('Error enviando por Baileys WhatsApp:', err);
      const confirmFallback = window.confirm(`No se pudo enviar directamente desde el servidor Baileys (${err.message}).\n\n¿Deseas abrir WhatsApp Web como alternativa?`);
      if (confirmFallback) {
        window.open(`https://wa.me/51${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
      }
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const isClientBaja = contractData && (contractData.client.status === 'inactive' || contractData.client.status === 'baja');

  // =========================================================================
  // LAS 13 PÁGINAS EXACTAS DEL CONTRATO PROPORCIONADO POR EL USUARIO
  // =========================================================================
  const contratoPages = contractData ? [
    // PÁGINA 1
    (
      <Box key={1}>
        <Typography variant="h5" sx={{ fontWeight: 900, textAlign: 'center', mb: 1, color: '#0f172a' }}>
          CONTRATO DE PRESTACIÓN DEL SERVICIO DE ACCESO A INTERNET
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center', mb: 2, color: '#6366f1' }}>
          YinoWISP
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Versión del contrato:</strong> 1.0<br/>
          <strong>Fecha de vigencia:</strong> {contractData.contractDate}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, textAlign: 'justify', lineHeight: 1.6 }}>
          Conste por el presente documento el <strong>Contrato de Prestación del Servicio de Acceso a Internet</strong>, celebrado entre:
        </Typography>
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderLeft: '4px solid #6366f1', mb: 2, fontSize: '0.825rem' }}>
          <strong>EL OPERADOR: JEAN PABLO CASTILLO GARCIA</strong>, con RUC N.° <strong>10742852640</strong>, quien desarrolla sus actividades comerciales bajo el nombre de <strong>YinoWISP</strong>, en adelante YinoWISP o EL OPERADOR;<br/><br/>
          y,<br/><br/>
          <strong>EL ABONADO: {contractData.client.fullName}</strong>, la persona cuyos datos se encuentran consignados en la Ficha de Contratación, Instalación y Entrega de Equipos, que forma parte integrante del presente contrato.
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>Ambas partes acuerdan lo siguiente:</Typography>
        <div className="section-title">PRIMERA. OBJETO DEL CONTRATO</div>
        <p className="clause">1.1. El presente contrato tiene por objeto establecer las condiciones bajo las cuales YinoWISP presta a EL ABONADO el servicio de acceso a Internet mediante tecnología inalámbrica WISP.</p>
        <p className="clause">1.2. El servicio será prestado en la dirección registrada en la Ficha de Contratación, Instalación y Entrega de Equipos (<strong>{contractData.client.address}, {contractData.client.district} - {contractData.client.province}</strong>).</p>
        <p className="clause">1.3. Las condiciones particulares del servicio, incluyendo plan, tarifa, fecha de contratación, fecha de vencimiento, equipos y modalidad de adquisición o alquiler, constarán en dicha ficha.</p>
        <p className="clause">1.4. La Ficha de Contratación, Instalación y Entrega de Equipos forma parte integrante del presente contrato.</p>
        <div className="section-title">SEGUNDA. ÁMBITO Y FACTIBILIDAD DEL SERVICIO</div>
        <p className="clause">2.1. El servicio se encuentra sujeto a disponibilidad y factibilidad técnica.</p>
        <p className="clause">2.2. YinoWISP podrá realizar una evaluación técnica previa antes de instalar o trasladar el servicio.</p>
        <p className="clause">2.3. La cobertura estará determinada por las características de la red inalámbrica, ubicación del domicilio, línea de vista, distancia, obstáculos, interferencias y demás condiciones técnicas.</p>
      </Box>
    ),
    // PÁGINA 2
    (
      <Box key={2}>
        <div className="section-title">TERCERA. PLANES Y VELOCIDADES</div>
        <p className="clause">3.1. Los planes comerciales inicialmente disponibles son:</p>
        <table style={{ margin: '8px 0 16px 0' }}>
          <thead>
            <tr><th>Plan</th><th>Velocidad máxima contratada</th><th>Tarifa mensual</th></tr>
          </thead>
          <tbody>
            <tr><td>Plan 20 Mbps</td><td>20 Mbps</td><td>S/ 50.00</td></tr>
            <tr><td>Plan 30 Mbps</td><td>30 Mbps</td><td>S/ 70.00</td></tr>
            <tr><td>Plan 50 Mbps</td><td>50 Mbps</td><td>S/ 100.00</td></tr>
          </tbody>
        </table>
        <p className="clause">3.2. Las tarifas indicadas corresponden a los planes comerciales vigentes al momento de elaboración de este contrato.</p>
        <p className="clause">3.3. La velocidad máxima contratada y la velocidad mínima garantizada deberán ser informadas a EL ABONADO conforme a las disposiciones regulatorias aplicables.</p>
        <p className="clause">3.4. Para los servicios de Internet de banda ancha, la velocidad mínima garantizada será la establecida por la normativa vigente. Actualmente, el marco aplicable establece como referencia un mínimo del <strong>70%</strong> de la velocidad contratada de subida y bajada, sujeto a las condiciones y metodología de medición correspondientes.</p>
        <p className="clause">3.5. Para efectos ilustrativos:</p>
        <table style={{ margin: '8px 0 16px 0' }}>
          <thead>
            <tr><th>Plan</th><th>Máxima contratada</th><th>Mínima garantizada 70%</th></tr>
          </thead>
          <tbody>
            <tr><td>20 Mbps</td><td>20 Mbps</td><td>14 Mbps</td></tr>
            <tr><td>30 Mbps</td><td>30 Mbps</td><td>21 Mbps</td></tr>
            <tr><td>50 Mbps</td><td>50 Mbps</td><td>35 Mbps</td></tr>
          </tbody>
        </table>
        <p className="clause">3.6. La medición de velocidad deberá realizarse conforme a la metodología establecida por la normativa aplicable y, cuando corresponda, mediante conexión cableada al equipo de entrega, con condiciones adecuadas de medición.</p>
        <div className="section-title">CUARTA. NATURALEZA DEL SERVICIO INALÁMBRICO</div>
        <p className="clause">4.1. EL ABONADO declara conocer que el servicio se presta mediante tecnología inalámbrica.</p>
        <p className="clause">4.2. Las condiciones radioeléctricas pueden verse afectadas por:</p>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6 }}>
          a) Obstáculos físicos.<br/>b) Distancia.<br/>c) Interferencias.<br/>d) Condiciones climáticas.<br/>e) Cambios en el entorno.<br/>f) Saturación radioeléctrica.
        </Box>
      </Box>
    ),
    // PÁGINA 3
    (
      <Box key={3}>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
          g) Características de los equipos.<br/>h) Factores externos no controlables por YinoWISP.
        </Box>
        <p className="clause">4.3. Estas condiciones no implican por sí mismas una falla contractual, siempre que YinoWISP cumpla con las condiciones de calidad y obligaciones que correspondan.</p>
        <div className="section-title">QUINTA. TARIFA Y FORMA DE PAGO</div>
        <p className="clause">5.1. EL ABONADO deberá pagar la tarifa correspondiente al plan contratado (<strong>{contractData.service.planName}: S/ {contractData.service.price}</strong>).</p>
        <p className="clause">5.2. La fecha de vencimiento podrá variar entre abonados y será registrada en la cuenta del cliente (Día {contractData.client.paymentDay} de cada mes).</p>
        <p className="clause">5.3. Los medios de pago podrán incluir:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • Yape.<br/>• Plin.<br/>• Mercado Pago.<br/>• Transferencia bancaria.<br/>• Efectivo.<br/>• Otros medios habilitados por YinoWISP.
        </Box>
        <p className="clause">5.4. EL ABONADO podrá realizar pagos adelantados.</p>
        <p className="clause">5.5. Los pagos adelantados serán registrados y aplicados al periodo correspondiente, respetando las disposiciones legales aplicables.</p>
        <div className="section-title">SEXTA. SUSPENSIÓN POR FALTA DE PAGO</div>
        <p className="clause">6.1. En caso de falta de pago, YinoWISP podrá suspender el servicio conforme a las condiciones contractuales y normativa aplicable.</p>
        <p className="clause">6.2. La suspensión no extingue las obligaciones económicas previamente generadas.</p>
        <p className="clause">6.3. La suspensión por falta de pago no generará una penalidad de reconexión.</p>
        <p className="clause">6.4. Una vez regularizada la situación del abonado, YinoWISP procederá con la reactivación conforme a sus procedimientos y normativa aplicable.</p>
        <p className="clause">6.5. La suspensión por falta de pago no implica automáticamente la condonación de la deuda.</p>
        <p className="clause">6.6. Si la deuda permanece pendiente durante el tiempo establecido por las condiciones aplicables, YinoWISP podrá proceder con la terminación del servicio conforme a la normativa vigente.</p>
        <div className="section-title">SÉPTIMA. BAJA DEL SERVICIO</div>
        <p className="clause">7.1. EL ABONADO podrá solicitar la baja del servicio conforme a la normativa vigente.</p>
      </Box>
    ),
    // PÁGINA 4
    (
      <Box key={4}>
        <p className="clause">7.2. La solicitud podrá efectuarse mediante:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • WhatsApp.<br/>• Portal del cliente.<br/>• Correo electrónico.<br/>• Atención presencial.<br/>• Otros canales habilitados por YinoWISP.
        </Box>
        <p className="clause">7.3. La baja será procesada dentro de los plazos legalmente aplicables.</p>
        <p className="clause">7.4. La existencia de una deuda pendiente no impedirá la solicitud de baja, sin perjuicio de que las obligaciones económicas legítimamente generadas continúen siendo exigibles.</p>
        <p className="clause">7.5. La baja no transfiere a EL ABONADO la propiedad de los equipos que pertenezcan a YinoWISP.</p>
        <p className="clause">7.6. En caso de equipos alquilados, EL ABONADO deberá permitir su retiro o realizar su devolución conforme al procedimiento correspondiente.</p>
        <div className="section-title">OCTAVA. DURACIÓN</div>
        <p className="clause">8.1. El presente contrato tendrá duración indeterminada.</p>
        <p className="clause">8.2. No se establece un periodo mínimo obligatorio de permanencia.</p>
        <p className="clause">8.3. EL ABONADO podrá solicitar la terminación del servicio conforme a la cláusula de baja.</p>
        <div className="section-title">NOVENA. INSTALACIÓN</div>
        <p className="clause">9.1. YinoWISP realizará la instalación del servicio en el domicilio registrado.</p>
        <p className="clause">9.2. La instalación ordinaria no tendrá costo para EL ABONADO mientras dicha condición comercial se encuentre vigente.</p>
        <p className="clause">9.3. La instalación podrá incluir, según corresponda:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • Antena CPE.<br/>• Router Wi-Fi.<br/>• Fuente PoE.<br/>• Cableado.<br/>• Conectores.<br/>• Soportes.<br/>• Otros accesorios necesarios.
        </Box>
        <p className="clause">9.4. Los equipos y materiales efectivamente entregados serán registrados en la Ficha de Instalación.</p>
      </Box>
    ),
    // PÁGINA 5
    (
      <Box key={5}>
        <div className="section-title">DÉCIMA. EQUIPOS</div>
        <p className="clause">10.1. Los equipos podrán ser adquiridos por EL ABONADO o entregados en modalidad de alquiler, según lo registrado en la Ficha de Instalación.</p>
        <p className="clause">10.2. Cuando EL ABONADO compre un equipo, este será de su propiedad una vez cumplidas las condiciones de adquisición.</p>
        <p className="clause">10.3. Cuando el equipo sea alquilado, continuará siendo propiedad de YinoWISP.</p>
        <p className="clause">10.4. EL ABONADO deberá cuidar los equipos que pertenezcan a YinoWISP.</p>
        <p className="clause">10.5. EL ABONADO no podrá vender, transferir, prestar, desarmar o modificar equipos propiedad de YinoWISP.</p>
        <div className="section-title">DÉCIMA PRIMERA. PÉRDIDA O DAÑO DE EQUIPOS</div>
        <p className="clause">11.1. Si un equipo propiedad de YinoWISP se pierde, es sustraído, destruido o queda inutilizable por causa atribuible a EL ABONADO, este deberá asumir el valor correspondiente.</p>
        <p className="clause">11.2. El valor de cada equipo será registrado individualmente en la Ficha de Instalación y Entrega.</p>
        <p className="clause">11.3. El cobro deberá corresponder al equipo efectivamente afectado.</p>
        <p className="clause">11.4. El desgaste normal derivado del uso razonable no será considerado daño imputable al abonado.</p>
        <div className="section-title">DÉCIMA SEGUNDA. MANIPULACIÓN</div>
        <p className="clause">12.1. EL ABONADO no deberá desmontar, abrir, reorientar, reparar, modificar ni alterar los equipos instalados por YinoWISP sin autorización.</p>
        <p className="clause">12.2. Si una avería fuera ocasionada por manipulación no autorizada, YinoWISP podrá determinar las acciones técnicas correspondientes.</p>
        <p className="clause">12.3. EL ABONADO podrá solicitar autorización para reemplazar el router por uno propio.</p>
        <div className="section-title">DÉCIMA TERCERA. INSTALACIÓN ELÉCTRICA</div>
        <p className="clause">13.1. EL ABONADO será responsable de mantener condiciones adecuadas en la instalación eléctrica interna utilizada para alimentar los equipos.</p>
        <p className="clause">13.2. Se recomienda utilizar instalaciones eléctricas seguras y sistemas de protección adecuados.</p>
        <p className="clause">13.3. YinoWISP no será responsable por daños ocasionados exclusivamente por:</p>
      </Box>
    ),
    // PÁGINA 6
    (
      <Box key={6}>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
          a) Sobretensiones.<br/>b) Descargas eléctricas.<br/>c) Rayos.<br/>d) Instalaciones eléctricas defectuosas.<br/>e) Manipulación de terceros.<br/>f) Otros eventos externos no atribuibles a YinoWISP.
        </Box>
        <div className="section-title">DÉCIMA CUARTA. SOPORTE TÉCNICO</div>
        <p className="clause">14.1. YinoWISP brindará soporte mediante los canales habilitados.</p>
        <p className="clause">14.2. Los canales podrán incluir: WhatsApp, Teléfono, Portal del cliente.</p>
        <p className="clause">14.3. El horario ordinario de atención será: <strong>Lunes a domingo: 8:00 a. m. a 5:00 p. m.</strong></p>
        <p className="clause">14.4. Los reportes recibidos fuera del horario podrán ser registrados para atención posterior.</p>
        <p className="clause">14.5. YinoWISP procurará atender los reportes dentro de un plazo máximo de 48 horas, sujeto a disponibilidad técnica, acceso al domicilio, condiciones climáticas, seguridad y demás circunstancias que puedan afectar la atención.</p>
        <div className="section-title">DÉCIMA QUINTA. MANTENIMIENTO</div>
        <p className="clause">15.1. YinoWISP podrá realizar mantenimiento preventivo, correctivo y de ampliación.</p>
        <p className="clause">15.2. Cuando sea posible, se comunicará previamente al abonado.</p>
        <p className="clause">15.3. Los mantenimientos podrán ocasionar interrupciones temporales.</p>
        <div className="section-title">DÉCIMA SEXTA. INTERRUPCIONES</div>
        <p className="clause">16.1. El servicio puede verse afectado por:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • Fallas eléctricas.<br/>• Averías.<br/>• Mantenimientos.<br/>• Interferencias.<br/>• Robo.<br/>• Vandalismo.<br/>• Daños causados por terceros.
        </Box>
      </Box>
    ),
    // PÁGINA 7
    (
      <Box key={7}>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 2 }}>
          • Fenómenos naturales.<br/>• Condiciones climáticas.<br/>• Problemas de proveedores.<br/>• Cortes de enlaces.<br/>• Fuerza mayor.<br/>• Caso fortuito.
        </Box>
        <p className="clause">16.2. YinoWISP realizará las acciones razonables para restablecer el servicio.</p>
        <p className="clause">16.3. Las interrupciones y compensaciones estarán sujetas a las condiciones y obligaciones establecidas por la normativa vigente.</p>
        <div className="section-title">DÉCIMA SÉPTIMA. DESCUENTO O PRORRATEO</div>
        <p className="clause">17.1. Cuando corresponda una compensación económica por una interrupción del servicio, YinoWISP podrá aplicar un ajuste proporcional al periodo afectado.</p>
        <p className="clause">17.2. Dicho concepto podrá aparecer en el comprobante como: <strong>“Descuento por prorrateo por interrupción del servicio”</strong> o una descripción equivalente.</p>
        <p className="clause">17.3. El cálculo se realizará considerando el periodo efectivamente afectado y las reglas aplicables.</p>
        <div className="section-title">DÉCIMA OCTAVA. TRASLADO</div>
        <p className="clause">18.1. EL ABONADO podrá solicitar el traslado del servicio.</p>
        <p className="clause">18.2. El traslado estará sujeto a cobertura y factibilidad técnica.</p>
        <p className="clause">18.3. No se cobrará un cargo ordinario por traslado mientras dicha condición comercial permanezca vigente.</p>
        <p className="clause">18.4. Si la nueva dirección no cuenta con cobertura o factibilidad, el traslado no podrá ejecutarse.</p>
        <p className="clause">18.5. EL ABONADO podrá decidir continuar con el servicio en la dirección original o solicitar la baja.</p>
        <p className="clause">18.6. Si existen equipos alquilados y se produce la baja, deberán ser devueltos.</p>
        <div className="section-title">DÉCIMA NOVENA. USO RESIDENCIAL Y PROHIBICIÓN DE REVENTA</div>
        <p className="clause">19.1. El servicio contratado está destinado al uso personal/residencial de EL ABONADO en la dirección contratada.</p>
      </Box>
    ),
    // PÁGINA 8
    (
      <Box key={8}>
        <p className="clause">19.2. EL ABONADO no podrá:</p>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
          a) Revender el servicio.<br/>b) Compartirlo comercialmente con terceros.<br/>c) Distribuirlo a otras viviendas.<br/>d) Utilizarlo para operar un WISP.<br/>e) Redistribuirlo mediante antenas.<br/>f) Cobrar a terceros por el acceso.<br/>g) Utilizarlo en establecimientos distintos al domicilio contratado.
        </Box>
        <p className="clause">19.3. Cualquier uso comercial o servicio de distribución a terceros requerirá una contratación específica y autorización previa de YinoWISP.</p>
        <div className="section-title">VIGÉSIMA. USO ILEGAL O ABUSIVO</div>
        <p className="clause">20.1. EL ABONADO deberá utilizar el servicio conforme a la legislación peruana.</p>
        <p className="clause">20.2. Se prohíbe utilizar el servicio para:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • Fraude.<br/>• Ataques informáticos.<br/>• Malware.<br/>• Spam masivo.<br/>• Acceso no autorizado.<br/>• Ataques de denegación de servicio.<br/>• Actividades ilícitas.<br/>• Daño deliberado a redes o sistemas.<br/>• Actividades que afecten gravemente a otros usuarios.
        </Box>
        <p className="clause">20.3. Ante situaciones de riesgo, seguridad o uso ilícito, YinoWISP podrá adoptar las medidas permitidas por la normativa aplicable.</p>
        <div className="section-title">VIGÉSIMA PRIMERA. WI-FI DENTRO DEL DOMICILIO</div>
        <p className="clause">21.1. La velocidad del enlace de Internet no equivale necesariamente a la velocidad obtenida por Wi-Fi.</p>
        <p className="clause">21.2. El rendimiento Wi-Fi puede verse afectado por: Distancia, Paredes, Interferencias, Cantidad de dispositivos, Capacidad del dispositivo, Banda de frecuencia, Ubicación del router, Configuración de la red.</p>
        <p className="clause">21.3. YinoWISP no garantiza una intensidad de señal uniforme en todos los ambientes de la vivienda.</p>
      </Box>
    ),
    // PÁGINA 9
    (
      <Box key={9}>
        <div className="section-title">VIGÉSIMA SEGUNDA. FACTURACIÓN</div>
        <p className="clause">22.1. YinoWISP emitirá los comprobantes que correspondan conforme a su régimen tributario y las disposiciones de SUNAT.</p>
        <p className="clause">22.2. Para los clientes comprendidos en el alcance de este contrato, se emitirá la boleta de venta cuando corresponda.</p>
        <p className="clause">22.3. EL ABONADO acepta recibir sus comprobantes mediante medios electrónicos habilitados.</p>
        <div className="section-title">VIGÉSIMA TERCERA. DATOS PERSONALES</div>
        <p className="clause">23.1. YinoWISP podrá tratar los datos personales necesarios para administrar la relación contractual.</p>
        <p className="clause">23.2. Podrán registrarse:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 1 }}>
          • Nombres.<br/>• Apellidos.<br/>• DNI.<br/>• Dirección.<br/>• Teléfono.<br/>• Correo.<br/>• Datos de pago.<br/>• Datos de instalación.<br/>• Dirección IP.<br/>• MAC.<br/>• Número de serie.<br/>• Información técnica.<br/>• Historial de atención.
        </Box>
        <p className="clause">23.3. El tratamiento se realizará conforme a la Ley N.° 29733 y su Reglamento vigente, aprobado por Decreto Supremo N.° 016-2024-JUS.</p>
        <p className="clause">23.4. Los datos podrán utilizarse para: a) Prestación del servicio. b) Soporte técnico. c) Facturación. d) Cobranza. e) Seguridad. f) Atención de solicitudes. g) Cumplimiento de obligaciones legales.</p>
        <p className="clause">23.5. Las comunicaciones promocionales estarán sujetas a la autorización y reglas correspondientes.</p>
      </Box>
    ),
    // PÁGINA 10
    (
      <Box key={10}>
        <div className="section-title">VIGÉSIMA CUARTA. COMUNICACIONES</div>
        <p className="clause">24.1. EL ABONADO autoriza las comunicaciones necesarias para administrar el servicio.</p>
        <p className="clause">24.2. Estas podrán incluir: Avisos de vencimiento, Confirmaciones de pago, Suspensiones, Reactivaciones, Mantenimientos, Incidencias, Comprobantes, Avisos administrativos.</p>
        <p className="clause">24.3. Las comunicaciones comerciales estarán sujetas a las reglas aplicables de protección de datos personales.</p>
        <div className="section-title">VIGÉSIMA QUINTA. CONTRATACIÓN DIGITAL</div>
        <p className="clause">25.1. El contrato podrá aceptarse electrónicamente mediante el portal YinoWISP.</p>
        <p className="clause">25.2. La aceptación podrá registrarse mediante: Identificación del cliente, Fecha, Hora, Dirección IP, Versión del contrato, Registro de aceptación, Otros datos técnicos necesarios.</p>
        <p className="clause">25.3. EL ABONADO deberá poder consultar la versión del contrato que aceptó.</p>
        <div className="section-title">VIGÉSIMA SEXTA. CONTRATACIÓN FÍSICA</div>
        <p className="clause">26.1. El contrato podrá ser firmado físicamente.</p>
        <p className="clause">26.2. La firma del abonado, junto con la Ficha de Instalación, acreditará la documentación contractual entregada y aceptada, sin perjuicio de los requisitos legales aplicables.</p>
        <div className="section-title">VIGÉSIMA SÉPTIMA. OBLIGACIONES DE YinoWISP</div>
        <p className="clause">YinoWISP deberá:</p>
      </Box>
    ),
    // PÁGINA 11
    (
      <Box key={11}>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
          a) Prestar el servicio contratado.<br/>b) Atender reportes.<br/>c) Gestionar incidencias.<br/>d) Mantener su infraestructura en condiciones razonables.<br/>e) Informar mantenimientos cuando corresponda.<br/>f) Emitir comprobantes.<br/>g) Proteger los datos personales.<br/>h) Atender solicitudes y reclamos.<br/>i) Cumplir la normativa aplicable.
        </Box>
        <div className="section-title">VIGÉSIMA OCTAVA. OBLIGACIONES DEL ABONADO</div>
        <p className="clause">EL ABONADO deberá:</p>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
          a) Pagar oportunamente.<br/>b) Proporcionar información verdadera.<br/>c) Mantener sus datos actualizados.<br/>d) Cuidar los equipos alquilados.<br/>e) No manipular equipos sin autorización.<br/>f) No revender el servicio.<br/>g) No utilizarlo ilegalmente.<br/>h) Permitir el acceso técnico cuando corresponda.<br/>i) Devolver equipos alquilados.<br/>j) Informar fallas oportunamente.
        </Box>
        <div className="section-title">VIGÉSIMA NOVENA. RECLAMOS Y SOLICITUDES</div>
        <p className="clause">29.1. EL ABONADO podrá presentar solicitudes, consultas y reclamos mediante los canales habilitados.</p>
        <p className="clause">29.2. Los procedimientos y plazos se regirán por la normativa aplicable.</p>
        <p className="clause">29.3. Ninguna cláusula de este contrato limita los derechos que la legislación reconozca a EL ABONADO.</p>
        <div className="section-title">TRIGÉSIMA. MODIFICACIONES</div>
        <p className="clause">30.1. YinoWISP podrá actualizar sus condiciones comerciales y contractuales conforme a la normativa vigente.</p>
        <p className="clause">30.2. Cuando una modificación requiera comunicación al abonado, se realizará por los mecanismos correspondientes.</p>
        <p className="clause">30.3. Ninguna modificación podrá desconocer derechos establecidos por normas imperativas.</p>
      </Box>
    ),
    // PÁGINA 12
    (
      <Box key={12}>
        <div className="section-title">TRIGÉSIMA PRIMERA. TERMINACIÓN</div>
        <p className="clause">31.1. El contrato podrá terminar por:</p>
        <Box sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6, mb: 1 }}>
          a) Solicitud de baja.<br/>b) Acuerdo de las partes.<br/>c) Falta de pago, cuando corresponda.<br/>d) Causales permitidas legalmente.<br/>e) Otras causas establecidas por la normativa aplicable.
        </Box>
        <p className="clause">31.2. La terminación no extingue obligaciones económicas legítimamente generadas.</p>
        <p className="clause">31.3. La terminación tampoco elimina la obligación de devolver equipos propiedad de YinoWISP.</p>
        <div className="section-title">TRIGÉSIMA SEGUNDA. PREVALENCIA NORMATIVA</div>
        <p className="clause">32.1. Las disposiciones de este contrato deberán interpretarse conforme a la legislación peruana y las normas regulatorias aplicables.</p>
        <p className="clause">32.2. Si una cláusula contradice una norma imperativa, prevalecerá la norma correspondiente.</p>
        <p className="clause">32.3. La actual Norma de Condiciones de Uso de OSIPTEL debe ser considerada como parte del marco regulatorio aplicable al servicio cuando corresponda.</p>
        <div className="section-title">TRIGÉSIMA TERCERA. DOCUMENTOS INTEGRANTES</div>
        <p className="clause">Forman parte del presente contrato:</p>
        <Box sx={{ pl: 3, fontSize: '0.8rem', lineHeight: 1.5, mb: 2 }}>
          <strong>Anexo 1:</strong> Ficha de Contratación, Instalación y Entrega de Equipos.<br/>
          <strong>Anexo 2:</strong> Acta de Devolución de Equipos.<br/>
          <strong>Anexo 3:</strong> Versión del contrato aceptada por EL ABONADO.
        </Box>
        <div className="section-title">TRIGÉSIMA CUARTA. ACEPTACIÓN</div>
        <p className="clause">EL ABONADO declara haber leído y comprendido las condiciones del presente contrato y acepta su contenido.</p>
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '6px', my: 2, fontSize: '0.8rem' }}>
          <strong>DATOS DEL ABONADO:</strong><br/>
          Nombres y apellidos: <strong>{contractData.client.fullName}</strong><br/>
          DNI: <strong>{contractData.client.dni}</strong>
        </Box>
      </Box>
    ),
    // PÁGINA 13
    (
      <Box key={13}>
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '6px', mb: 3, fontSize: '0.8rem', lineHeight: 1.7 }}>
          Teléfono: <strong>{contractData.client.phone}</strong><br/>
          Correo electrónico: <strong>{contractData.client.email}</strong><br/>
          Dirección del servicio: <strong>{contractData.client.address}</strong><br/>
          Plan: <strong>{contractData.service.planName}</strong><br/>
          Tarifa mensual: <strong>S/ {contractData.service.price}</strong><br/>
          Fecha de contratación: <strong>{contractData.contractDate}</strong><br/>
          Fecha de vencimiento: <strong>Día {contractData.client.paymentDay} de cada mes</strong>
        </Box>

        <div className="section-title">FIRMAS Y CONFORMIDAD DE LAS PARTES</div>

        <Box sx={{ mt: 6, pt: 2, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <Box sx={{ width: '44%' }}>
            {contractData.installationTechnicalData.clientSignatureUrl ? (
              <Box component="img" src={contractData.installationTechnicalData.clientSignatureUrl} sx={{ height: 60, mb: 1 }} alt="Firma Abonado" />
            ) : <Box sx={{ height: 60, mb: 1, borderBottom: '1px dashed #cbd5e1' }} />}
            <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>EL ABONADO</Typography>
              <Typography variant="body2">Nombre: {contractData.client.fullName}</Typography>
              <Typography variant="caption" display="block" color="textSecondary">DNI: {contractData.client.dni}</Typography>
              <Typography variant="caption" color="textSecondary">Fecha: {contractData.contractDate}</Typography>
            </Box>
          </Box>

          <Box sx={{ width: '44%' }}>
            <Box sx={{ height: 60, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#6366f1', fontWeight: 700 }}>YinoWISP Sello Oficial</Typography>
            </Box>
            <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>YinoWISP</Typography>
              <Typography variant="body2">Jean Pablo Castillo Garcia</Typography>
              <Typography variant="caption" display="block" color="textSecondary">RUC N.° 10742852640</Typography>
              <Typography variant="caption" color="textSecondary">Fecha: {contractData.contractDate}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  ] : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '20px', p: 1, maxHeight: '96vh' }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon sx={{ color: '#6366f1' }} />
          <Typography variant="h6" fontWeight="bold">Expediente Contractual y Técnico OSIPTEL</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Selector de Documentos / Pestañas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#f8fafc' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="📜 Contrato Principal (13 Págs)" sx={{ textTransform: 'none', fontWeight: 700 }} />
          <Tab icon={<BuildIcon />} iconPosition="start" label="🛠️ Anexo 1: Ficha de Instalación (10 Págs)" sx={{ textTransform: 'none', fontWeight: 700 }} />
          <Tab 
            icon={<ReturnIcon />} 
            iconPosition="start" 
            label="📦 Anexo 2: Acta de Devolución (6 Págs)" 
            sx={{ 
              textTransform: 'none', 
              fontWeight: 700, 
              color: isClientBaja ? '#dc2626' : '#64748b' 
            }} 
          />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ bgcolor: '#f1f5f9', p: { xs: 1, sm: 2.5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 4, textAlign: 'center', fontWeight: 'bold' }}>
            ⚠️ {error}
          </Typography>
        ) : contractData ? (
          <Box id="contract-printable-area" ref={printRef}>

            {/* BARRA DE NAVEGACIÓN PÁGINA POR PÁGINA (PAGINADOR INTERACTIVO) */}
            {activeTab === 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', p: 1.5, borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PrevIcon />}
                  disabled={contratoPageIndex === 0}
                  onClick={() => setContratoPageIndex(prev => Math.max(0, prev - 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Anterior
                </Button>
                <Chip 
                  label={`PÁGINA ${contratoPageIndex + 1} DE 13`} 
                  color="primary" 
                  sx={{ fontWeight: 900, fontSize: '0.85rem', px: 1.5 }} 
                />
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<NextIcon />}
                  disabled={contratoPageIndex === 12}
                  onClick={() => setContratoPageIndex(prev => Math.min(12, prev + 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Siguiente
                </Button>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', p: 1.5, borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PrevIcon />}
                  disabled={anexo1PageIndex === 0}
                  onClick={() => setAnexo1PageIndex(prev => Math.max(0, prev - 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Anterior
                </Button>
                <Chip 
                  label={`ANEXO 1: PÁGINA ${anexo1PageIndex + 1} DE 10`} 
                  color="primary" 
                  sx={{ fontWeight: 900, fontSize: '0.85rem', px: 1.5 }} 
                />
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<NextIcon />}
                  disabled={anexo1PageIndex === 9}
                  onClick={() => setAnexo1PageIndex(prev => Math.min(9, prev + 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Siguiente
                </Button>
              </Box>
            )}

            {activeTab === 2 && isClientBaja && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', p: 1.5, borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PrevIcon />}
                  disabled={anexo2PageIndex === 0}
                  onClick={() => setAnexo2PageIndex(prev => Math.max(0, prev - 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Anterior
                </Button>
                <Chip 
                  label={`ANEXO 2: PÁGINA ${anexo2PageIndex + 1} DE 6`} 
                  color="error" 
                  sx={{ fontWeight: 900, fontSize: '0.85rem', px: 1.5 }} 
                />
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  endIcon={<NextIcon />}
                  disabled={anexo2PageIndex === 5}
                  onClick={() => setAnexo2PageIndex(prev => Math.min(5, prev + 1))}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                >
                  Página Siguiente
                </Button>
              </Box>
            )}

            {/* TAB 0: VISTA INDIVIDUAL PÁGINA POR PÁGINA DEL CONTRATO */}
            {activeTab === 0 && (
              <Paper className="a4-page" elevation={3} sx={{ p: 4, bgcolor: 'white', borderRadius: '12px', minHeight: '680px', position: 'relative' }}>
                <Box className="page-header" sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #6366f1', pb: 1, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900}>{contractData.companyInfo.name}</Typography>
                    <Typography variant="caption" color="textSecondary">RUC: {contractData.companyInfo.ruc} | CONTRATO WISP</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip label={contractData.contractNumber} color="primary" size="small" sx={{ fontWeight: 800 }} />
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ fontWeight: 700 }}>Página {contratoPageIndex + 1} de 13</Typography>
                  </Box>
                </Box>

                {contratoPages[contratoPageIndex]}

                <Box className="page-footer" sx={{ mt: 4, pt: 1, borderTop: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b' }}>
                  <span>YinoWISP - Contrato del Servicio de Internet</span>
                  <span>Abonado: {contractData.client.fullName}</span>
                  <span>Página {contratoPageIndex + 1} de 13</span>
                </Box>
              </Paper>
            )}

            {/* TAB 1: ANEXO 1 CON CHECKBOXES DE EQUIPOS Y ACCESORIOS */}
            {activeTab === 1 && (
              <Paper className="a4-page" elevation={3} sx={{ p: 4, bgcolor: 'white', borderRadius: '12px', minHeight: '680px', position: 'relative' }}>
                <Box className="page-header" sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #6366f1', pb: 1, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900}>{contractData.companyInfo.name}</Typography>
                    <Typography variant="caption" color="textSecondary">ANEXO 1: FICHA DE CONTRATACIÓN E INSTALACIÓN TÉCNICA</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip label={contractData.contractNumber} color="primary" size="small" sx={{ fontWeight: 800 }} />
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ fontWeight: 700 }}>Página {anexo1PageIndex + 1} de 10</Typography>
                  </Box>
                </Box>

                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 900, textTransform: 'uppercase', mb: 2, color: '#0f172a' }}>
                  ANEXO 1: FICHA DE INSTALACIÓN Y ENTREGA DE EQUIPOS (PÁG {anexo1PageIndex + 1}/10)
                </Typography>

                {anexo1PageIndex === 0 && (
                  <Box>
                    <div className="section-title">1. DATOS GENERALES DEL ABONADO Y UBICACIÓN GPS</div>
                    <Grid container spacing={1.5} sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <Grid item xs={12} sm={6}><strong>Cliente Titular:</strong> {contractData.client.fullName}</Grid>
                      <Grid item xs={12} sm={6}><strong>DNI / RUC:</strong> {contractData.client.dni}</Grid>
                      <Grid item xs={12} sm={6}><strong>Teléfono:</strong> {contractData.client.phone}</Grid>
                      <Grid item xs={12} sm={6}><strong>Email:</strong> {contractData.client.email}</Grid>
                      <Grid item xs={12} sm={12}><strong>Dirección de Instalación:</strong> {contractData.client.address} ({contractData.client.district} - {contractData.client.province})</Grid>
                      <Grid item xs={12} sm={6}><strong>Coordenadas GPS:</strong> {contractData.client.coordinates}</Grid>
                      <Grid item xs={12} sm={6}><strong>IP Asignada / Queue:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{contractData.client.ipAddress}</span></Grid>
                    </Grid>
                  </Box>
                )}

                {anexo1PageIndex === 1 && (
                  <Box>
                    <div className="section-title">2. PLAN DE INTERNET Y VELOCIDAD MÍNIMA GARANTIZADA 70%</div>
                    <Grid container spacing={1.5} sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <Grid item xs={12} sm={6}><strong>Plan Contratado:</strong> {contractData.service.planName}</Grid>
                      <Grid item xs={12} sm={6}><strong>Tarifa Mensual:</strong> S/ {contractData.service.price}</Grid>
                      <Grid item xs={12} sm={6}><strong>Velocidad Máxima:</strong> {contractData.service.downloadSpeed} / {contractData.service.uploadSpeed}</Grid>
                      <Grid item xs={12} sm={6}><strong>70% Garantizado OSIPTEL:</strong> {contractData.service.guaranteedDownloadSpeed} / {contractData.service.guaranteedUploadSpeed}</Grid>
                    </Grid>
                  </Box>
                )}

                {/* PÁGINA 3 Y 4 CON CHECKBOXES INTERACTIVOS Y IMPRIMIBLES DE EQUIPOS */}
                {(anexo1PageIndex === 2 || anexo1PageIndex === 3) && (
                  <Box>
                    <div className="section-title">3. INVENTARIO DE HARDWARE Y ACCESORIOS CON CHECKBOXES MARCABLES</div>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Marque los checkboxes correspondientes a los equipos y materiales entregados en la instalación:
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#f8fafc', mb: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.cpeAntenna} onChange={() => handleToggleAnexo1Check('cpeAntenna')} color="primary" />}
                            label={<Typography variant="body2" sx={{ fontWeight: 700 }}>[☑] Antena CPE Receptor Inalámbrico ({contractData.equipment.antennaModel})</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.wifiRouter} onChange={() => handleToggleAnexo1Check('wifiRouter')} color="primary" />}
                            label={<Typography variant="body2" sx={{ fontWeight: 700 }}>[☑] Router Wi-Fi Principal ({contractData.equipment.wifiRouterModel})</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.poeInjector} onChange={() => handleToggleAnexo1Check('poeInjector')} color="primary" />}
                            label={<Typography variant="body2">Fuente Inyectora PoE de Alimentación</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.utpOutdoorCable} onChange={() => handleToggleAnexo1Check('utpOutdoorCable')} color="primary" />}
                            label={<Typography variant="body2">Cable UTP Categoría 5e / 6 Exterior UV</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.shieldedRj45} onChange={() => handleToggleAnexo1Check('shieldedRj45')} color="primary" />}
                            label={<Typography variant="body2">Conectores RJ45 Blindados Metálicos</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.metallicMast} onChange={() => handleToggleAnexo1Check('metallicMast')} color="primary" />}
                            label={<Typography variant="body2">Mástil / Soporte Metálico Galvanizado</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.fixingKit} onChange={() => handleToggleAnexo1Check('fixingKit')} color="primary" />}
                            label={<Typography variant="body2">Kit de Pernos Tirefón y Tarugos 3/8"</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo1Checkboxes.groundingSystem} onChange={() => handleToggleAnexo1Check('groundingSystem')} color="primary" />}
                            label={<Typography variant="body2">Sistema de Puesta a Tierra Eléctrica</Typography>}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                )}

                {anexo1PageIndex >= 4 && (
                  <Box>
                    <div className="section-title">PRUEBAS Y FIRMAS DE CONFORMIDAD DE INSTALACIÓN</div>
                    <Grid container spacing={1.5} sx={{ mb: 3, fontSize: '0.85rem' }}>
                      <Grid item xs={12} sm={6}><strong>Técnico Instalador:</strong> {contractData.installationTechnicalData.technicianName}</Grid>
                      <Grid item xs={12} sm={6}><strong>Fecha Instalación:</strong> {contractData.installationTechnicalData.installationDate}</Grid>
                      <Grid item xs={12} sm={6}><strong>Nivel de Señal:</strong> <span style={{ color: '#059669', fontWeight: 700 }}>{contractData.installationTechnicalData.signalDb}</span></Grid>
                      <Grid item xs={12} sm={6}><strong>Latencia Ping:</strong> <span style={{ color: '#059669', fontWeight: 700 }}>{contractData.installationTechnicalData.pingMs}</span></Grid>
                    </Grid>

                    <Box sx={{ mt: 8, pt: 4, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                      <Box sx={{ width: '42%' }}>
                        <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{contractData.installationTechnicalData.technicianName}</Typography>
                          <Typography variant="caption" color="textSecondary">FIRMA TÉCNICO INSTALADOR</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ width: '42%' }}>
                        {contractData.installationTechnicalData.clientSignatureUrl ? (
                          <Box component="img" src={contractData.installationTechnicalData.clientSignatureUrl} sx={{ height: 50, mb: 1 }} alt="Firma Cliente" />
                        ) : null}
                        <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{contractData.client.fullName}</Typography>
                          <Typography variant="caption" display="block" color="textSecondary">DNI: {contractData.client.dni}</Typography>
                          <Typography variant="caption" color="textSecondary">FIRMA CONFORMIDAD ABONADO</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box className="page-footer" sx={{ mt: 4, pt: 1, borderTop: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b' }}>
                  <span>YinoWISP - Anexo 1 Ficha de Instalación</span>
                  <span>Cliente: {contractData.client.fullName}</span>
                  <span>Página {anexo1PageIndex + 1} de 10</span>
                </Box>
              </Paper>
            )}

            {/* TAB 2: ANEXO 2 CON CHECKBOXES DE DEVOLUCIÓN Y ESTADO FÍSICO */}
            {activeTab === 2 && (
              <Box>
                {!isClientBaja && initialTab !== 2 ? (
                  <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#fffbebfb', border: '1px solid #fde68a', borderRadius: '16px', my: 2 }}>
                    <WarningIcon sx={{ fontSize: 54, color: '#d97706', mb: 1.5 }} />
                    <Typography variant="h6" fontWeight="bold" color="#92400e">
                      Anexo 2 (Acta de Devolución) No Aplica para Clientes Activos
                    </Typography>
                    <Typography variant="body2" color="#b45309" sx={{ mt: 1, maxWidth: 580, mx: 'auto', lineHeight: 1.6 }}>
                      El <strong>Anexo 2 (Acta de Devolución de Equipos)</strong> es un documento legal que se genera únicamente cuando el abonado procesa la <strong>Baja Definitiva del Servicio de Internet</strong> para certificar el retiro de la antena CPE y el Router Wi-Fi.<br/><br/>
                      Estado actual del cliente <strong>{contractData.client.fullName}</strong>: <Chip label={contractData.client.status.toUpperCase()} color="success" size="small" sx={{ fontWeight: 800 }} />
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="subtitle2" color="#78350f" sx={{ mb: 2, fontWeight: 700 }}>
                      ¿Deseas dar de baja el servicio de este cliente ahora?
                    </Typography>

                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => {
                        onClose();
                        if (onOpenDeactivation) {
                          onOpenDeactivation(contractData.client);
                        }
                      }}
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, px: 3, py: 1 }}
                    >
                      ⚠️ Dar de Baja el Servicio de este Cliente
                    </Button>
                  </Paper>
                ) : (
                  <Paper className="a4-page" elevation={3} sx={{ p: 4, bgcolor: 'white', borderRadius: '12px', minHeight: '680px', position: 'relative' }}>
                    <Box className="page-header" sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #dc2626', pb: 1, mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900}>{contractData.companyInfo.name}</Typography>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>ANEXO 2: ACTA DE DEVOLUCIÓN DE EQUIPOS</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip label="ACTA DE BAJA" color="error" size="small" sx={{ fontWeight: 800 }} />
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ fontWeight: 700 }}>Página {anexo2PageIndex + 1} de 6</Typography>
                      </Box>
                    </Box>

                    <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 900, textTransform: 'uppercase', mb: 2, color: '#dc2626' }}>
                      ANEXO 2: ACTA DE DEVOLUCIÓN Y RECEPCIÓN DE EQUIPOS (PÁG {anexo2PageIndex + 1}/6)
                    </Typography>

                    <div className="section-title">CHECKLIST DE EQUIPOS DEVOLTOS Y EVALUACIÓN DE ESTADO</div>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#fff5f5', mb: 3 }}>
                      <Typography variant="subtitle2" color="error" sx={{ fontWeight: 800, mb: 1.5 }}>
                        1. Equipos en Alquiler Recuperados:
                      </Typography>
                      <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.cpeAntenna} onChange={() => handleToggleAnexo2Check('cpeAntenna')} color="error" />}
                            label={<Typography variant="body2" sx={{ fontWeight: 700 }}>[☑] Antena CPE ({contractData.equipment.antennaModel})</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.wifiRouter} onChange={() => handleToggleAnexo2Check('wifiRouter')} color="error" />}
                            label={<Typography variant="body2" sx={{ fontWeight: 700 }}>[☑] Router Wi-Fi ({contractData.equipment.wifiRouterModel})</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.poeInjector} onChange={() => handleToggleAnexo2Check('poeInjector')} color="error" />}
                            label={<Typography variant="body2">Fuente Inyectora PoE</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.metallicMast} onChange={() => handleToggleAnexo2Check('metallicMast')} color="error" />}
                            label={<Typography variant="body2">Mástil / Soporte Metálico</Typography>}
                          />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography variant="subtitle2" color="error" sx={{ fontWeight: 800, mb: 1.5 }}>
                        2. Evaluación del Estado Físico:
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.conditionBueno} onChange={() => handleToggleAnexo2Check('conditionBueno')} color="success" />}
                            label={<Typography variant="body2">[✓] Operativo / Estado Bueno</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Checkbox checked={anexo2Checkboxes.conditionDamaged} onChange={() => handleToggleAnexo2Check('conditionDamaged')} color="error" />}
                            label={<Typography variant="body2">[❌] Dañado por mal uso</Typography>}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    <Box sx={{ mt: 8, pt: 4, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                      <Box sx={{ width: '42%' }}>
                        <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>JEAN PABLO CASTILLO GARCIA</Typography>
                          <Typography variant="caption" color="textSecondary">TÉCNICO / RECEPCIÓN</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ width: '42%' }}>
                        <Box sx={{ borderTop: '2px solid #1e293b', pt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{contractData.client.fullName}</Typography>
                          <Typography variant="caption" display="block" color="textSecondary">DNI: {contractData.client.dni}</Typography>
                          <Typography variant="caption" color="textSecondary">FIRMA CONFORMIDAD</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box className="page-footer" sx={{ mt: 4, pt: 1, borderTop: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b' }}>
                      <span>YinoWISP - Anexo 2 Acta de Devolución</span>
                      <span>Cliente: {contractData.client.fullName}</span>
                      <span>Página {anexo2PageIndex + 1} de 6</span>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}

            {/* OPORTUNIDAD OCULTA PARA IMPRIMIR TODAS LAS PÁGINAS CONTINUAS AL DESCARGAR PDF */}
            <Box id="contract-all-pages-printable" sx={{ display: 'none' }}>
              {activeTab === 0 && contratoPages.map((pg, idx) => (
                <div key={idx} className="a4-page">
                  <div className="page-header">
                    <div>
                      <h4 style={{ margin: 0, fontWeight: 900 }}>{contractData.companyInfo.name}</h4>
                      <span style={{ fontSize: '9px', color: '#64748b' }}>RUC: {contractData.companyInfo.ruc} | OPERADOR REGISTRADO OSIPTEL</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 800 }}>{contractData.contractNumber}</span><br/>
                      <span style={{ fontSize: '9px', color: '#64748b' }}>Página {idx + 1} de 13</span>
                    </div>
                  </div>
                  {pg}
                  <div className="page-footer">
                    <span>YinoWISP - Expediente Contractual OSIPTEL</span>
                    <span>Cliente: {contractData.client.fullName} ({contractData.client.dni})</span>
                    <span>Página {idx + 1} / 13</span>
                  </div>
                </div>
              ))}
            </Box>

          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={emittingContract ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
            onClick={handleEmitAndSaveContract}
            disabled={emittingContract || !contractData}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, mr: 1 }}
          >
            {emittingContract ? 'Guardando...' : '💾 Emitir / Guardar en Ficha'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={sendingWhatsapp ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={handleSendWhatsapp}
            disabled={sendingWhatsapp || !contractData}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, mr: 1, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#128C7E' } }}
          >
            {sendingWhatsapp ? 'Enviando por VPS...' : 'Enviar por WhatsApp'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrintAll}
            disabled={!contractData}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
          >
            Imprimir / Descargar PDF Completo
          </Button>
        </Box>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#64748b', '&:hover': { bgcolor: '#475569' } }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractModal;
